import type { Canon, Evaluation, Profile, ReadingList, Work } from '../../core/types.ts'

export type ListSummary = Omit<ReadingList, 'passwordHash'> & { hasPassword: boolean; canEdit: boolean }

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly body?: unknown) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const message = (body as { error?: string } | null)?.error ?? `Požadavek selhal (${res.status})`
    throw new ApiError(message, res.status, body)
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T)
}

/** Admin režim se z prohlížeče jen čte — přepíná se tokenem z Makefile. */
export type AdminMode = { on: boolean; until: string | null }

export const api = {
  adminMode: () => request<AdminMode>('/api/admin/mode'),

  canons: () => request<{ id: string; name: string }[]>('/api/canons'),
  canon: (id: string) => request<Canon>(`/api/canons/${id}`),
  setRecommendations: (canonId: string, recommendations: { workId: string; recommended: boolean; note: string | null }[]) =>
    request<{ ok: true; recommended: number }>(`/api/canons/${canonId}/recommendations`, {
      method: 'PUT',
      body: JSON.stringify({ recommendations }),
    }),
  updateWork: (
    canonId: string,
    workId: string,
    patch: Pick<Work, 'author' | 'title' | 'formId' | 'firstPublished' | 'origin' | 'originalLanguage'>,
  ) => request<Work>(`/api/canons/${canonId}/works/${workId}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  profiles: () => request<Profile[]>('/api/profiles'),
  saveProfile: (p: Profile) => request<Profile>(`/api/profiles/${p.id}`, { method: 'PUT', body: JSON.stringify(p) }),
  createProfile: (p: Profile) => request<Profile>('/api/profiles', { method: 'POST', body: JSON.stringify(p) }),

  lists: () => request<ListSummary[]>('/api/lists'),
  list: (id: string) => request<ListSummary>(`/api/lists/${id}`),
  createList: (input: { name: string; studentName: string | null; canonId: string; profileId?: string; password?: string | null }) =>
    request<ListSummary>('/api/lists', { method: 'POST', body: JSON.stringify(input) }),
  updateList: (id: string, patch: { name?: string; studentName?: string | null; selectedWorkIds?: string[] }) =>
    request<ListSummary>(`/api/lists/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteList: (id: string) => request<void>(`/api/lists/${id}`, { method: 'DELETE' }),
  unlock: (id: string, password: string) =>
    request<{ ok: true }>(`/api/lists/${id}/unlock`, { method: 'POST', body: JSON.stringify({ password }) }),
  setPassword: (id: string, newPassword: string | null, currentPassword?: string) =>
    request<{ ok: true; hasPassword: boolean }>(`/api/lists/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword, currentPassword }),
    }),
  evaluate: (id: string) => request<Evaluation>(`/api/lists/${id}/evaluate`),

  importPreview: (pdf: File) => {
    const form = new FormData()
    form.append('pdf', pdf)
    return request<ImportPreview>('/api/imports/preview', { method: 'POST', body: form })
  },
  importCommit: (payload: ImportCommit) =>
    request<{ id: string; name: string; works: number }>('/api/imports/commit', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}

export type ImportRow = {
  n: number
  author: string | null
  title: string
  sourcePage: number
  formId: 'prose' | 'poetry' | 'drama' | null
  firstPublished: number | null
  origin: 'cs' | 'world' | null
  originalLanguage: string | null
  known: boolean
  confidence: { form: string; firstPublished: string; origin: string }
}

export type ImportPreview = {
  suggestedName: string
  total: number
  known: number
  missing: number
  rows: ImportRow[]
}

export type ImportCommit = {
  id: string
  name: string
  rows: {
    n: number
    author: string | null
    title: string
    sourcePage?: number
    formId: 'prose' | 'poetry' | 'drama'
    firstPublished: number | null
    origin: 'cs' | 'world'
    originalLanguage: string | null
  }[]
}
